import { initializeApp } from 'firebase/app'
/* import { Functions, getFunctions, httpsCallable } from 'firebase/functions' */
import { Auth, getAuth, signInWithEmailAndPassword, UserCredential } from 'firebase/auth'

import { getIsDevelopment, getIsProduction } from 'utils/get-environment'
/* import LocationInfo from 'types/LocationInfo' */

export const firebaseUser = { email: process.env.NEXT_PUBLIC_FIREBASE_EMAIL, password: process.env.NEXT_PUBLIC_FIREBASE_PASSWORD }
export const firebaseStagingConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: 'once-upon-staging.firebaseapp.com',
  databaseURL: 'https://once-upon-staging.firebaseio.com',
  projectId: 'once-upon-staging',
  storageBucket: 'once-upon-staging.appspot.com',
  messagingSenderId: '759590667561',
  appId: '1:759590667561:web:09b5fcaf0a7b48018ce338',
  measurementId: 'G-E17K015H5Q',
}

const firebaseDevConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: 'once-upon-dev.firebaseapp.com',
  projectId: 'once-upon-dev',
}
const firebaseProductionConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: 'once-upon-4f42c.firebaseapp.com',
  databaseURL: 'https://once-upon-4f42c.firebaseio.com',
  projectId: 'once-upon-4f42c',
  storageBucket: 'once-upon-4f42c.appspot.com',
  messagingSenderId: '516538798264',
  appId: '1:516538798264:web:05e989a68e7b486c2ea44d',
  measurementId: 'G-TZ43WR8TE7',
}

// Initialize Firebase app
const config = getIsProduction() ? firebaseProductionConfig : getIsDevelopment() ? firebaseDevConfig : firebaseStagingConfig
const app = initializeApp(config)

// Initialize Firebase services
/* let functions: Functions | null = null */
let auth: Auth | null = null

/* export const getFirebaseFunctions = () => {
  if (!functions) {
    functions = getFunctions(app)
  }
  return functions
} */

export const getFirebaseAuth = () => {
  if (!auth) {
    auth = getAuth(app)
  }
  return auth
}

/* export const getLocationInfo = async (): Promise<LocationInfo> => {
  const apiCall = httpsCallable(getFirebaseFunctions(), 'apiV1GetUserLocationInfo')
  const result = await apiCall()
  const locationInfo: LocationInfo = result.data as LocationInfo
  return locationInfo
} */

/* export const getUserCountry = async (): Promise<string> => {
  const locationInfo = await getLocationInfo()
  return locationInfo?.country
} */

/**
 * Returns a Firebase ID token for the shared service user.
 *
 * This function ensures that:
 * - Only one login request is made at a time (even if called multiple times in parallel)
 * - Token is fetched after login (or immediately if the user is already logged in)
 *
 * Helps avoid:
 * - Multiple sign-in attempts to Firebase (which can trigger auth throttling)
 * - Redundant requests and race conditions during app startup
 */

let loginInProgress: Promise<UserCredential> | null = null
let tokenFetchInProgress: Promise<string> | null = null
let jwtToken: string | null = null
let jwtTokenExpiry: number | null = null

export const getJwtToken = async (): Promise<string> => {
  const authInstance = getFirebaseAuth()

  // If already cached and still valid (with 5 min buffer), return it
  const now = Date.now()
  if (jwtToken && jwtTokenExpiry && now < jwtTokenExpiry - 5 * 60 * 1000) {
    return jwtToken
  }

  // If no user is signed in, initiate login (only once)
  if (!authInstance.currentUser) {
    if (!loginInProgress) {
      loginInProgress = signInWithEmailAndPassword(
        authInstance,
        process.env.NEXT_PUBLIC_FIREBASE_EMAIL!,
        process.env.NEXT_PUBLIC_FIREBASE_PASSWORD!
      ).finally(() => {
        // Clear loginInProgress regardless of success/failure
        loginInProgress = null
      })
    }

    // Wait for login to complete if it's in progress
    await loginInProgress
  }

  // ✅ Guard against duplicate token fetches
  if (!tokenFetchInProgress) {
    tokenFetchInProgress = (async () => {
      const token = await authInstance.currentUser!.getIdToken()
      const tokenInfo = await authInstance.currentUser!.getIdTokenResult()
      jwtToken = token
      jwtTokenExpiry = new Date(tokenInfo.expirationTime).getTime()
      return token
    })().finally(() => {
      tokenFetchInProgress = null
    })
  }

  return tokenFetchInProgress
}
